import { parseDataSchema, parseUISchema } from "@certn/application";
import { renderers } from "@certn/forms";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { formatBoolean, formatDate, parseObject } from "@certn/utils";
import { css } from "@emotion/css";
import {
  formatIs,
  or,
  rankWith,
  schemaTypeIs,
  uiTypeIs,
} from "@jsonforms/core";
import {
  JsonForms,
  withJsonFormsControlProps,
  withJsonFormsLayoutProps,
} from "@jsonforms/react";
import { useTranslation } from "react-i18next";

import { SummaryItem } from "./SummaryItem";
import { Schemas } from "../../types";

interface CredentialsJsonFormProps {
  credentialDefinition: Schemas["CredentialDefinition"];
  credential: Schemas["CredentialJson"];
}

const createCustomRenderers = (t: (key: string) => string) => [
  {
    tester: rankWith(20, schemaTypeIs("boolean")),
    renderer: withJsonFormsControlProps((props) => (
      <SummaryItem label={props.label} value={formatBoolean(t, props.data)} />
    )),
  },
  {
    tester: rankWith(20, formatIs("date")),
    renderer: withJsonFormsControlProps((props) => (
      <SummaryItem label={props.label} value={formatDate(props.data, "PPP")} />
    )),
  },
  {
    tester: rankWith(
      20,
      or(
        schemaTypeIs("string"),
        schemaTypeIs("number"),
        schemaTypeIs("integer")
      )
    ),
    renderer: withJsonFormsControlProps((props) => (
      <SummaryItem
        label={props.label}
        value={
          props.data === undefined || props.data === null || props.data === ""
            ? "-"
            : String(props.data)
        }
      />
    )),
  },
  {
    tester: rankWith(20, uiTypeIs("VerticalLayout")),
    renderer: withJsonFormsLayoutProps((props) => {
      return <BaseLayout {...props} className={styles.verticalLayout} />;
    }),
  },
  ...renderers,
];

export const CredentialsJsonForm = ({
  credentialDefinition,
  credential,
}: CredentialsJsonFormProps) => {
  const { t } = useTranslation();

  if (!credential || !credentialDefinition) {
    return null;
  }

  const { credentialSubject, evidence } = credential;
  const {
    schema: { data_schema, ui_schema },
  } = credentialDefinition;
  const data = {
    ...parseObject(credentialSubject),
    ...parseObject(evidence),
  };

  const customRenderers = createCustomRenderers(t);

  return (
    <div className={styles.form}>
      <JsonForms
        data={data}
        schema={parseDataSchema(data_schema)}
        uischema={parseUISchema(ui_schema)}
        renderers={customRenderers}
        readonly
      />
    </div>
  );
};

const styles = {
  form: css({ paddingLeft: "var(--space-4)", paddingRight: "var(--space-4)" }),
  verticalLayout: css({ gap: "var(--space-0)" }),
};
